import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { twJoin } from "tailwind-merge";

import { ExternalLink } from "src/base-components/ExternalLink";
import { StatusIcon } from "src/base-components/StatusIcon";
import { AgentVersion, AgentVersionStatus } from "src/clients/agent-management";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { UserNameInfo } from "src/flowContainer/versionEditedInfo/UsernameInfo";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToAgentVersionPage } from "src/router/urls";
import { formatDate } from "src/utils/datetime";

type Props = {
  agentId: string;
  version: AgentVersion;
  selected?: boolean;
  onClick: () => void;
};

const StatusIndicator: React.FC<{ status: AgentVersionStatus }> = ({
  status,
}) => {
  const isPublished = status === AgentVersionStatus.PUBLISHED;
  return <StatusIcon variant={isPublished ? "published" : "draft"} />;
};

type FooterProps = {
  userId: string;
  isPublished: boolean;
  date: string;
};

const VersionCardFooter: React.FC<FooterProps> = ({
  date,
  userId,
  isPublished,
}) => {
  const formattedDate = date !== "" ? formatDate(date) : null;

  const editInfo = (
    <>
      <div className="flex text-gray-800">
        <UserNameInfo userId={userId || ""} withAvatar />
      </div>
      {formattedDate && (
        <>
          on
          <span className="text-gray-800">{formattedDate}</span>
        </>
      )}
    </>
  );

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 font-inter-normal-12px text-gray-600"
      data-loc="agent-version-editor-info"
    >
      {isPublished ? (
        <>published by {editInfo}</>
      ) : (
        <>last edited by {editInfo}</>
      )}
    </div>
  );
};

export const AgentVersionCard: React.FC<Props> = ({
  agentId,
  selected,
  version,
  onClick,
}) => {
  const { orgId, workspace } = useAuthoringContext();
  const isPublished = version.status === AgentVersionStatus.PUBLISHED;
  const date = isPublished
    ? version.metadata.published_at
    : version.metadata.updated_at;
  const userId = isPublished
    ? version.metadata.published_by_id || version.metadata.created_by_id
    : version.metadata.updated_by_id || version.metadata.created_by_id;

  const versionUrl = `${getBaseUrl()}${getUrlToAgentVersionPage(orgId, workspace.id, agentId, version.id)}`;

  return (
    <div
      className={twJoin(
        "cursor-pointer rounded-lg border p-4",
        selected ? "border-indigo-400" : "border-gray-200",
      )}
      data-loc={`agent-version-card-${version.name}`}
      tabIndex={-1}
      onClick={onClick}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <StatusIndicator status={version.status} />
          <span className="font-inter-semibold-13px text-gray-800">
            {version.name}
          </span>
        </div>
        <Tooltip placement="bottom-end" title="Open in new tab" asChild>
          <div>
            <ExternalLink
              href={versionUrl}
              onClick={(e) => e.stopPropagation()}
            >
              <Icon icon={faUpRightFromSquare} size="xs" />
            </ExternalLink>
          </div>
        </Tooltip>
      </div>
      <p
        className={twJoin(
          "mb-4 line-clamp-2 font-inter-normal-12px leading-5",
          version.description ? "text-gray-500" : "text-gray-400",
        )}
      >
        {version.description || "No description"}
      </p>
      <VersionCardFooter
        date={date || ""}
        isPublished={isPublished}
        userId={userId || ""}
      />
    </div>
  );
};
